import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Paper from '@material-ui/core/Paper';

import Button from 'Components/UI/Button';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import Typography from 'Components/UI/Typography';
import useMetaFormBuilder from 'Hooks/useMetaFormBuilder';

import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaFormFieldList from './MetaFormFieldList';
import ConfirmationDialog from '../../../ConfirmationDialog';
import MetaLayout from './MetaLayout';

const useStyles = buildUseStyles('MetaFormInline', (theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  rootFullWidth: {
    width: '100%',
  },
  title: {},
  fieldList: {},
  fieldListRow: {},
  fieldListControl: {},
  fieldListControlNotFirst: {},
  actions: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    justifyContent: 'space-between',
  },
  leftActionButton: {
    marginRight: theme.spacing(1),
  },
  confirmDialog: {
    minWidth: '250px',
  },
  saveButton: {},
  neutralButton: {},
  neutralAction: {},
  actionButtons: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  hide: {
    display: 'none',
  },
  rootPaddingNone: {
    padding: theme.spacing(0),
  },
  rootMarginNone: {
    margin: theme.spacing(0),
  },
}));

/**
 *
 * @deprecated Use MetaResourceRenderer
 */
export default function MetaFormInline({
  className,
  component,
  resource: resourceProp,
  onDelete: onDeleteProp,
  onFailure,
  onSubmit: onSubmitProp,
  onSubmitBefore: onSubmitBeforeProp,
  onChange: onChangeProp,
  autoSave,
  hideEmptyFields,
  rows,
  title,
  readonly,
  confirmDeleteMessage,
  confirmSubmitMessage,
  children,
  layout,
  LayoutProps,
  FieldListProps,
  submitLabelOverride,
  onNeutral,
  neutralLabel,
  savingLabelOverride,
  onCancel,
  noIndividualSubmitButtons,
  noIndividualDeleteButtons,
  onAdd,
  onRemove,
  onErrorLinkClick,
  exportAttributesAsStrings,
  fieldsImmediatelyRequired,
  inlineSubmit,
  index,
  metaOverrideSet,
  errorsOverride,
  fullWidth,
  padding,
  margin,
  useRoundedButtons,
  SubmitButtonProps,
  ActionProps,
  useResourceArgs,
  allowSubmitDisable,
  noExtraMarkup,
  ...rest
}) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const {
    eventTrackingNodeRef,
    resource,
    onChange,
    onDelete,
    onSubmit,
    saving,
    errors,
  } = useMetaFormBuilder({
    onDelete: onDeleteProp,
    onFailure,
    onSubmitBefore: onSubmitBeforeProp,
    onSubmit: onSubmitProp === null ? null : (res, args) => onSubmitProp(res, index, args),
    resource: resourceProp,
    onChange: onChangeProp,
    autoSave,
    inlineSubmit,
    exportAttributesAsStrings,
    fieldsImmediatelyRequired,
    useResourceArgs,
  });

  const Component = component;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const onDeleteConfirmationOpen = useCallback(() => setShowDeleteConfirmation(true), []);
  const onDeleteConfirmationClose = useCallback(() => setShowDeleteConfirmation(false), []);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const onSubmitConfirmationOpen = useCallback(() => setShowSubmitConfirmation(true), []);
  const onSubmitConfirmationClose = useCallback(() => setShowSubmitConfirmation(false), []);
  const getSubmitLabel = () => {
    if (saving) {
      return savingLabelOverride || 'Saving';
    }

    return submitLabelOverride || 'Submit';
  };

  const showNeutralButton = onNeutral && neutralLabel;
  const showCancelButton = onCancel;
  const showDeleteButton = onDelete && !noIndividualDeleteButtons;
  const showSubmitButton = onSubmit && !inlineSubmit && !noIndividualSubmitButtons;
  const showActions = showCancelButton || showDeleteButton || showSubmitButton;

  const ButtonComponent = useRoundedButtons ? RoundedButton : Button;

  const requestDelete = confirmDeleteMessage ? onDeleteConfirmationOpen : onDelete;
  const requestSubmit = confirmSubmitMessage ? onSubmitConfirmationOpen : onSubmit;

  return (
    <Component
      data-form-id={`${resource.type()}-${resource.id()}`}
      className={classNames(classes.root, className, {
        [classes.rootFullWidth]: fullWidth === true,
        [classes.rootPaddingNone]: padding === 'none',
        [classes.rootMarginNone]: margin === 'none',
      })}
      ref={eventTrackingNodeRef}
      {...rest}
    >
      {title && <Typography className={classes.title}>{title}</Typography>}
      {!layout ? (
        <MetaFormFieldList
          className={classes.fieldList}
          classes={{
            root: classes.fieldList,
            row: classes.fieldListRow,
            control: classes.fieldListControl,
            controlNotFirstInRow: classes.fieldListControlNotFirst,
          }}
          errors={errorsOverride || errors}
          hideEmptyFields={hideEmptyFields}
          onChange={onChange}
          resource={resource}
          rows={rows}
          layout={layout}
          onAdd={onAdd}
          onRemove={onRemove}
          onErrorLinkClick={onErrorLinkClick}
          metaOverrideSet={metaOverrideSet}
          {...FieldListProps}
        >
          {children}
        </MetaFormFieldList>
      ) : (
        <MetaLayout
          metaOverrideSet={metaOverrideSet}
          resource={resource}
          layout={layout}
          errors={errorsOverride || errors}
          onChange={onChange}
          onSubmit={requestSubmit}
          onDelete={requestDelete}
          saving={saving}
          inlineSubmit={inlineSubmit}
          LayoutProps={{
            index,
            noExtraMarkup,
            ...LayoutProps,
          }}
          onAdd={onAdd}
          onRemove={onRemove}
          onErrorLinkClick={onErrorLinkClick}
          autoSave={autoSave}
        />
      )}
      {!noExtraMarkup && !readonly && (
        <div
          className={classNames(classes.actions, {
            [classes.hide]: !showActions,
          })}
          {...ActionProps}
        >
          <div className={classes.neutralAction}>
            {showNeutralButton && (
              <ButtonComponent
                className={classes.neutralButton}
                variant="outlined"
                onClick={() => onNeutral(resource)}
                test="meta-form-neutral"
              >
                {neutralLabel}
              </ButtonComponent>
            )}
          </div>
          <div className={classes.actionButtons}>
            {showDeleteButton && (
              <ButtonComponent
                className={classes.leftActionButton}
                color="primaryGray"
                variant="text"
                onClick={requestDelete}
                test="meta-form-delete"
              >
                {'Delete'}
              </ButtonComponent>
            )}
            {showCancelButton && (
              <ButtonComponent
                className={classes.leftActionButton}
                color="secondary"
                onClick={onCancel}
                test="meta-form-cancel"
              >
                {'Cancel'}
              </ButtonComponent>
            )}
            {showSubmitButton && (
              <ButtonComponent
                className={classes.saveButton}
                test="meta-form-submit"
                color="secondary"
                disabled={allowSubmitDisable && (saving || !resource.isValid())}
                disableElevation
                onClick={requestSubmit}
                variant="contained"
                {...SubmitButtonProps}
              >
                {getSubmitLabel()}
              </ButtonComponent>
            )}
          </div>
        </div>
      )}
      {showDeleteConfirmation && confirmDeleteMessage && (
        <ConfirmationDialog
          className={classes.confirmDialog}
          message={confirmDeleteMessage}
          onCancel={onDeleteConfirmationClose}
          onConfirm={() => {
            onDeleteConfirmationClose();
            return onDelete();
          }}
        />
      )}
      {showSubmitConfirmation && confirmSubmitMessage && (
        <ConfirmationDialog
          className={classes.confirmDialog}
          message={confirmSubmitMessage}
          onCancel={onSubmitConfirmationClose}
          onConfirm={() => {
            onSubmitConfirmationClose();
            return onSubmit();
          }}
        />
      )}
    </Component>
  );
}

MetaFormInline.defaultProps = {
  className: '',
  component: Paper,
  elevation: 0,
  onDelete: null,
  onFailure: null,
  onSubmit: null,
  rows: [],
  title: null,
  confirmDeleteMessage: null,
  hideEmptyFields: false,
  children: null,
  submitLabelOverride: null,
  savingLabelOverride: null,
  layout: null,
  LayoutProps: {},
  FieldListProps: {},
  onNeutral: null,
  neutralLabel: '',
  onCancel: null,
  noIndividualSubmitButtons: null,
  noIndividualDeleteButtons: false,
  exportAttributesAsStrings: undefined,
  fieldsImmediatelyRequired: false,
  onAdd: null,
  onRemove: null,
  inlineSubmit: false,
  index: 0,
  metaOverrideSet: null,
  errorsOverride: null,
  onErrorLinkClick: null,
  fullWidth: false,
  padding: 'default',
  margin: 'default',
  useResourceArgs: {},
  allowSubmitDisable: true,
  noExtraMarkup: false,
};

MetaFormInline.propTypes = {
  className: PropTypes.string,
  component: () => {}, // TODO: Fix proptypes, should be allowed either a node like Paper or a div.
  elevation: PropTypes.number,
  hideEmptyFields: PropTypes.bool,
  resource: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onFailure: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  rows: PropTypes.array,
  layout: PropTypes.any,
  LayoutProps: PropTypes.object,
  FieldListProps: PropTypes.object,
  title: PropTypes.string,
  confirmDeleteMessage: PropTypes.string,
  children: PropTypes.node,
  submitLabelOverride: PropTypes.string,
  /**
   * Event handler for when neutral button is clicked. Also requires `neutralLabel.` If neither are present, neutral
   * button will not be displayed. `MetaFormInline` passes `resource` to the function.
   */
  onNeutral: PropTypes.func,
  /**
   * Label for neutral button.
   */
  neutralLabel: PropTypes.string,
  noIndividualSubmitButtons: PropTypes.bool,
  noIndividualDeleteButtons: PropTypes.bool,
  exportAttributesAsStrings: PropTypes.bool,
  fieldsImmediatelyRequired: PropTypes.bool,
  savingLabelOverride: PropTypes.string,
  inlineSubmit: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  index: PropTypes.number,
  metaOverrideSet: PropTypes.object,
  errorsOverride: PropTypes.array,
  fullWidth: PropTypes.bool,
  padding: PropTypes.oneOf(['default', 'none']),
  margin: PropTypes.oneOf(['default', 'none']),
  useResourceArgs: PropTypes.object,
  onErrorLinkClick: PropTypes.func,
  allowSubmitDisable: PropTypes.bool,
  noExtraMarkup: PropTypes.bool,
};
